/**
 * Validation rules for Seazen Policies and Trainings uploads (must match backend POLICY_TRAINING_*).
 */

import axios from 'axios';
import { getAuthHeaders } from './auth';

export const POLICY_TRAINING_MAX_BYTES = 100 * 1024 * 1024;
export const POLICY_TRAINING_MAX_FILES = 5;

export const POLICY_TRAINING_ALLOWED_MIME = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'video/mp4',
  'video/webm',
  'video/quicktime',
]);

const POLICY_TRAINING_EXT_TO_MIME = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
};

export const POLICY_TRAINING_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.mp4,.webm,.mov';

export const POLICY_TRAINING_TYPES = [
  { value: 'policy', label: 'Policy' },
  { value: 'training', label: 'Training' },
  { value: 'hr_form', label: 'HR Form' },
];

export const effectivePolicyTrainingMime = (file) => {
  let t = (file.type || '').trim().toLowerCase();
  if (t === 'image/jpg' || t === 'image/pjpeg') t = 'image/jpeg';
  if (t && t !== 'application/octet-stream') return t;
  const name = (file.name || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot) : '';
  const fromExt = POLICY_TRAINING_EXT_TO_MIME[ext];
  if (fromExt) return fromExt;
  return t;
};

export const validatePolicyTrainingFile = (file) => {
  if (!file) {
    return 'Please select a file to upload.';
  }
  const mime = effectivePolicyTrainingMime(file);
  if (!POLICY_TRAINING_ALLOWED_MIME.has(mime)) {
    return `"${file.name}" is not a supported file type. Allowed: video, PDF, images, Word, and PowerPoint.`;
  }
  if (file.size > POLICY_TRAINING_MAX_BYTES) {
    return `"${file.name}" exceeds the 100 MB size limit.`;
  }
  return null;
};

export const validatePolicyTrainingFiles = (files, { maxFiles = POLICY_TRAINING_MAX_FILES } = {}) => {
  const list = Array.from(files || []).filter(Boolean);
  if (list.length === 0) {
    return 'Please select at least one file to upload.';
  }
  if (list.length > maxFiles) {
    return `You can attach at most ${maxFiles} files per entry.`;
  }
  for (const file of list) {
    const err = validatePolicyTrainingFile(file);
    if (err) return err;
  }
  return null;
};

export const formatPolicyTrainingFileSize = (bytes) => {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const isPolicyTrainingVideo = (contentType) => {
  const t = (contentType || '').toLowerCase();
  return t.startsWith('video/');
};

export const isPolicyTrainingPdf = (contentType) =>
  (contentType || '').toLowerCase() === 'application/pdf';

export const isPolicyTrainingImage = (contentType) => {
  const t = (contentType || '').toLowerCase();
  return t === 'image/png' || t === 'image/jpeg' || t === 'image/jpg' || t === 'image/gif';
};

export const isPolicyTrainingInAppViewable = (contentType) =>
  isPolicyTrainingPdf(contentType) ||
  isPolicyTrainingImage(contentType) ||
  isPolicyTrainingVideo(contentType);

/** Normalize attachments from API (supports legacy single `file`). */
export const normalizePolicyTrainingAttachments = (doc) => {
  if (!doc) return [];
  if (Array.isArray(doc.attachments) && doc.attachments.length > 0) {
    return doc.attachments.filter((a) => a && (a.file_url || a.fileUrl));
  }
  if (doc.file && (doc.file.file_url || doc.file.fileUrl)) {
    return [doc.file];
  }
  return [];
};

/** Admin and super may upload or edit policies/trainings. */
export const canManagePolicyTraining = (role) => role === 'super' || role === 'admin';

/** Admin and super may delete policies/trainings. */
export const canDeletePolicyTraining = (role) => role === 'super' || role === 'admin';

/** HR forms are downloadable by any viewer; policies/trainings stay admin/super. */
export const canDownloadPolicyTraining = (role, documentType) => {
  if ((documentType || '').trim().toLowerCase() === 'hr_form') return true;
  return role === 'super' || role === 'admin';
};

export const downloadPolicyTrainingAttachment = async (fileUrl, fileName) => {
  const headers = await getAuthHeaders();
  const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/policies-trainings/attachment-file?file_url=${encodeURIComponent(fileUrl)}&download=true`;
  const response = await axios.get(proxyUrl, {
    headers,
    responseType: 'blob',
  });
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data]);
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

/** Ghous account may view the Deleted tab for policies/trainings. */
export const canViewDeletedPolicyTraining = (userProfile) => {
  const allowedEmail = 'm.ghous@seazengroup.com';
  return (userProfile?.email || '').trim().toLowerCase() === allowedEmail.toLowerCase();
};

/** Ghous account may restore soft-deleted policies/trainings. */
export const canRestorePolicyTraining = (userProfile) => canViewDeletedPolicyTraining(userProfile);

export const parseApiErrorDetail = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) {
    return err?.message || 'Something went wrong. Please try again.';
  }
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.msg || JSON.stringify(item);
        return String(item);
      })
      .join(' ');
  }
  if (typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail);
  }
  return String(detail);
};
